// API Endpoints constants
export const ENDPOINTS = {
  // Auth
  AUTH: {
    LOGIN: '/auth/login',
    REGISTER: '/auth/register',
    LOGOUT: '/auth/logout',
    REFRESH: '/auth/refresh',
    ME: '/auth/me',
    FORGOT_PASSWORD: '/auth/forgot-password',
    RESET_PASSWORD: '/auth/reset-password',
    CHANGE_PASSWORD: '/auth/change-password',
    VERIFY_PASSWORD: '/auth/verify-password',
  },

  // Uploads (device photos, signatures - to object storage)
  UPLOADS: {
    BASE: '/uploads',
  },

  // Customers
  CUSTOMERS: {
    BASE: '/customers',
    BY_ID: (id: string) => `/customers/${id}`,
    BY_PHONE: (phone: string) => `/customers/by-phone/${phone}`,
    SEARCH: '/customers/search',
    DEVICES: (id: string) => `/customers/${id}/devices`,
    TICKETS: (id: string) => `/customers/${id}/tickets`,
    DEBTS: '/customers/debts',
  },

  // Devices
  DEVICES: {
    BASE: '/devices',
    BY_ID: (id: string) => `/devices/${id}`,
    BY_IMEI: (imei: string) => `/devices/by-imei/${imei}`,
    CREDENTIALS: (id: string) => `/devices/${id}/credentials`,
    REVEAL_CREDENTIALS: (id: string) => `/devices/${id}/credentials/reveal`,
    CREDENTIALS_HISTORY: (id: string) => `/devices/${id}/credentials/reveal-history`,
    PHOTOS: (id: string) => `/devices/${id}/photos`,
  },

  // Repair Tickets
  TICKETS: {
    BASE: '/repairtickets',
    BY_ID: (id: string) => `/repairtickets/${id}`,
    BY_NUMBER: (number: string) => `/repairtickets/by-number/${number}`,
    SEARCH: '/repairtickets/search',
    START_DIAGNOSIS: (id: string) => `/repairtickets/${id}/start-diagnosis`,
    DIAGNOSE: (id: string) => `/repairtickets/${id}/diagnose`,
    APPROVE: (id: string) => `/repairtickets/${id}/approve`,
    REJECT: (id: string) => `/repairtickets/${id}/reject`,
    CANCEL: (id: string) => `/repairtickets/${id}/cancel`,
    START: (id: string) => `/repairtickets/${id}/start`,
    COMPLETE: (id: string) => `/repairtickets/${id}/complete`,
    DELIVER: (id: string) => `/repairtickets/${id}/deliver`,
    WAITING_FOR_PARTS: (id: string) => `/repairtickets/${id}/waiting-for-parts`,
    PARTS_ARRIVED: (id: string) => `/repairtickets/${id}/parts-arrived`,
    NOTES: (id: string) => `/repairtickets/${id}/notes`,
    EDIT_NOTE: (id: string, noteId: string) => `/repairtickets/${id}/notes/${noteId}`,
    DOCUMENTATION_PHOTOS: (id: string) => `/repairtickets/${id}/documentation-photos`,
    INVOICE_LINK: (id: string) => `/repairtickets/${id}/invoice-link`,
    CREATE_YESHINVOICE: (id: string) => `/repairtickets/${id}/create-yesinvoice`,
    CREATE_YESHINVOICE_TAX_INVOICE: (id: string) => `/repairtickets/${id}/create-yesinvoice-tax-invoice`,
    CANCEL_TAX_INVOICE: (id: string) => `/repairtickets/${id}/cancel-tax-invoice`,
    SYNC_INVOICE_PAYMENT: (id: string) => `/repairtickets/${id}/sync-invoice-payment`,
    CREATE_YESHINVOICE_PAYMENT_LINK: (id: string) => `/repairtickets/${id}/create-yesinvoice-payment-link`,
    PAYMENT_LINK: (id: string) => `/repairtickets/${id}/payment-link`,
    RECEIPT_LINK: (id: string) => `/repairtickets/${id}/receipt-link`,
    RECEIPTS_PREVIEW: (id: string) => `/repairtickets/${id}/receipts/preview`,
    RECEIPTS: (id: string) => `/repairtickets/${id}/receipts`,
    ACCESSORIES: (id: string) => `/repairtickets/${id}/accessories`,
    CREATE_YESHINVOICE_RECEIPT: (id: string) => `/repairtickets/${id}/create-yesinvoice-receipt`,
    COLLECT_DIAGNOSIS_FEE: (id: string) => `/repairtickets/${id}/collect-diagnosis-fee`,
    ISSUE_DIAGNOSIS_FEE_DOCUMENT: (id: string) => `/repairtickets/${id}/issue-diagnosis-fee-document`,
    DIAGNOSIS_FEE_DOCUMENT: (id: string) => `/repairtickets/${id}/diagnosis-fee-document`,
    DIAGNOSIS_FEE_PAYMENT_LINK: (id: string) => `/repairtickets/${id}/diagnosis-fee-payment-link`,
    WARRANTY_CHECK: `/repairtickets/warranty-check`,
    UNREPAIRABLE: (id: string) => `/repairtickets/${id}/unrepairable`,
    PREMIUM: (id: string) => `/repairtickets/${id}/premium`,
    DAMAGE_REPORTS: (id: string) => `/repairtickets/${id}/damage-reports`,
    PARTS: (id: string) => `/repairtickets/${id}/parts`,
    PART_LINE: (id: string, lineId: string) => `/repairtickets/${id}/parts/${lineId}`,
    PAYMENTS: (id: string) => `/repairtickets/${id}/payments`,
    LABOR_COST: (id: string) => `/repairtickets/${id}/labor-cost`,
    BILLING_OVERRIDE: (id: string) => `/repairtickets/${id}/billing-override`,
    PRICE_OFFSET: (id: string) => `/repairtickets/${id}/price-offset`,
    WARRANTY: (id: string) => `/repairtickets/${id}/warranty`,
    PRICE_REVISION: (id: string) => `/repairtickets/${id}/price-revision`,
    PRICE_INCREASE: (id: string) => `/repairtickets/${id}/price-increase`,
    ACCEPT_PRICE_OVERRUN: (id: string) => `/repairtickets/${id}/accept-price-overrun`,
    REPAIR_ADDITION: (id: string) => `/repairtickets/${id}/repair-addition`,
    REPEAT_DECISION: (id: string) => `/repairtickets/${id}/repeat-decision`,
    LOANER_DEVICE: (id: string) => `/repairtickets/${id}/loaner-device`,
    LOANER_DEVICE_RETURN: (id: string) => `/repairtickets/${id}/loaner-device/return`,
    STATUS_COUNTS: '/repairtickets/status-counts',
    ASSIGN: (id: string) => `/repairtickets/${id}/assign`,
    UNASSIGN: (id: string) => `/repairtickets/${id}/unassign`,
  },

  // Technicians
  TECHNICIANS: {
    BASE: '/technicians',
    BY_ID: (id: string) => `/technicians/${id}`,
    ACTIVE: '/technicians/active',
    WORKLOAD: (id: string) => `/technicians/${id}/workload`,
  },

  // Parts / Inventory
  PARTS: {
    BASE: '/parts',
    BY_ID: (id: string) => `/parts/${id}`,
    BY_SKU: (sku: string) => `/parts/by-sku/${sku}`,
    LOW_STOCK: '/parts/low-stock',
    ADJUST_STOCK: (id: string) => `/parts/${id}/adjust-stock`,
  },

  // Loaner devices - lent to customers while their own device is being repaired
  LOANER_DEVICES: {
    BASE: '/loanerdevices',
    BY_ID: (id: string) => `/loanerdevices/${id}`,
    RETIRE: (id: string) => `/loanerdevices/${id}/retire`,
    REACTIVATE: (id: string) => `/loanerdevices/${id}/reactivate`,
  },

  // Dashboard
  DASHBOARD: {
    BASE: '/dashboard',
    STATS: '/dashboard/stats',
    RECENT_TICKETS: '/dashboard/recent-tickets',
    REVENUE: '/dashboard/revenue',
    TECHNICIAN_STATS: '/dashboard/technician-stats',
    DAILY_SUMMARY: '/dashboard/daily-summary',
    TICKET_STATS: '/dashboard/ticket-stats',
    REVENUE_STATS: '/dashboard/revenue-stats',
    TECHNICIAN_WORKLOAD: '/dashboard/technician-workload',
  },

  // Notifications (customer messaging — matches NotificationsController)
  NOTIFICATIONS: {
    SEND: '/notifications/send',
    STATUS_UPDATE: '/notifications/status-update',
    REMINDER: '/notifications/reminder',
    BULK: '/notifications/bulk',
    CUSTOMER_HISTORY: (customerId: string) => `/notifications/customer/${customerId}/history`,
    TICKET_HISTORY: (ticketId: string) => `/notifications/ticket/${ticketId}/history`,
    FAILED: '/notifications/failed',
  },

  // AI Features
  AI: {
    DIAGNOSE_TEXT: '/aidiagnosis/text',
    DIAGNOSE_IMAGES: '/aidiagnosis/images',
    DIAGNOSE_FULL: '/aidiagnosis/full',
    DIAGNOSE_FEEDBACK: '/aidiagnosis/feedback',
    PHRASE_FOR_CUSTOMER: '/aidiagnosis/phrase-for-customer',
    QUICK_INTAKE_PROCESS: '/aiquickintake/process',
    QUICK_INTAKE_CONTINUE: '/aiquickintake/continue',
    QUICK_INTAKE_QUESTIONS: '/aiquickintake/questions',
    QUICK_INTAKE_CREATE_TICKET: '/aiquickintake/create-ticket',
  },

  // Sales
  SALES: {
    BASE: '/sales',
    BY_ID: (id: string) => `/sales/${id}`,
    BY_NUMBER: (saleNumber: string) => `/sales/by-number/${saleNumber}`,
    FROM_TICKET: (ticketId: string) => `/sales/from-ticket/${ticketId}`,
    ITEMS: (id: string) => `/sales/${id}/items`,
    ITEM: (id: string, itemId: string) => `/sales/${id}/items/${itemId}`,
    COMPLETE: (id: string) => `/sales/${id}/complete`,
    VOID: (id: string) => `/sales/${id}/void`,
    REFUND: (id: string) => `/sales/${id}/refund`,
  },

  // Invoices
  INVOICES: {
    BASE: '/invoices',
    BY_ID: (id: string) => `/invoices/${id}`,
    BY_NUMBER: (invoiceNumber: string) => `/invoices/by-number/${invoiceNumber}`,
    FROM_SALE: (saleId: string) => `/invoices/from-sale/${saleId}`,
    FINALIZE: (id: string) => `/invoices/${id}/finalize`,
    SEND: (id: string) => `/invoices/${id}/send`,
    PAYMENTS: (id: string) => `/invoices/${id}/payments`,
    CANCEL: (id: string) => `/invoices/${id}/cancel`,
    EXTERNAL_REF: (id: string) => `/invoices/${id}/external-reference`,
    // Legacy endpoints
    CREATE_ICOUNT: '/invoices/icount',
    CREATE_YESINVOICE: '/invoices/yesinvoice',
    BY_TICKET: (ticketId: string) => `/invoices/ticket/${ticketId}`,
  },

  // Payments
  PAYMENTS: {
    BASE: '/payments',
    BY_ID: (id: string) => `/payments/${id}`,
    BY_REFERENCE: (reference: string) => `/payments/by-reference/${reference}`,
    START_PROCESSING: (id: string) => `/payments/${id}/start-processing`,
    COMPLETE: (id: string) => `/payments/${id}/complete`,
    FAIL: (id: string) => `/payments/${id}/fail`,
    CANCEL: (id: string) => `/payments/${id}/cancel`,
    REFUND: (id: string) => `/payments/${id}/refund`,
    PROCESS_REFUND: (id: string) => `/payments/${id}/process-refund`,
    PAYMENT_LINK: (id: string) => `/payments/${id}/payment-link`,
    RETRY: (id: string) => `/payments/${id}/retry`,
  },
  // Settings (tenant configuration) - no BILLING/SECURITY endpoints exist on the backend for
  // editable billing fields or any security policy (session/MFA/password) - removed rather
  // than left as dead constants pointing at routes that don't exist.
  SETTINGS: {
    BASE: '/settings',
    OPERATIONAL: '/operationalsettings',
    UPDATE: '/settings',
    INTEGRATIONS: '/settings/integrations',
    NOTIFICATIONS: '/settings/notifications',
    TERMS_PDF: '/settings/terms-pdf',
  },

  // Users (tenant users management)
  USERS: {
    BASE: '/users',
    BY_ID: (id: string) => `/users/${id}`,
    ROLES: '/users/roles',
    INVITE: '/users/invite',
    ACTIVATE: (id: string) => `/users/${id}/activate`,
    DEACTIVATE: (id: string) => `/users/${id}/deactivate`,
  },

  // Reports (data exports) - only REVENUE is referenced by useReports.ts; every other
  // report hook hardcodes its real literal path directly instead of using this object.
  REPORTS: {
    REVENUE: '/reports/revenue',
    PROFIT: '/reports/profit',
  },

  // Commissions
  COMMISSIONS: {
    BASE: '/commissions',
    BY_ID: (id: string) => `/commissions/${id}`,
    FOR_REPAIR: (ticketId: string) => `/commissions/for-repair/${ticketId}`,
    FOR_SALE: '/commissions/for-sale',
    BONUS: '/commissions/bonus',
    APPROVE: (id: string) => `/commissions/${id}/approve`,
    PAY: (id: string) => `/commissions/${id}/pay`,
    CANCEL: (id: string) => `/commissions/${id}/cancel`,
    BATCH_APPROVE: '/commissions/batch/approve',
    BATCH_PAY: '/commissions/batch/pay',
    TECHNICIAN_SUMMARY: (technicianId: string) => `/commissions/technician/${technicianId}/summary`,
    REPORT: '/commissions/report',
  },

  // Cash Management
  CASH: {
    // Registers
    REGISTERS: '/cash/registers',
    REGISTER_BY_ID: (id: string) => `/cash/registers/${id}`,
    REGISTER_DETAIL: (id: string) => `/cash/registers/${id}/detail`,
    // Shifts
    SHIFTS: '/cash/shifts',
    SHIFT_BY_ID: (id: string) => `/cash/shifts/${id}`,
    CURRENT_SHIFT: (registerId: string) => `/cash/registers/${registerId}/current-shift`,
    OPEN_SHIFT: '/cash/shifts/open',
    CLOSE_SHIFT: (id: string) => `/cash/shifts/${id}/close`,
    APPROVE_SHIFT: (id: string) => `/cash/shifts/${id}/approve`,
    // Cash movements
    CASH_IN: (shiftId: string) => `/cash/shifts/${shiftId}/cash-in`,
    CASH_OUT: (shiftId: string) => `/cash/shifts/${shiftId}/cash-out`,
    SAFE_DROP: (shiftId: string) => `/cash/shifts/${shiftId}/safe-drop`,
    SAFE_PICKUP: (shiftId: string) => `/cash/shifts/${shiftId}/safe-pickup`,
    PETTY_CASH: (shiftId: string) => `/cash/shifts/${shiftId}/petty-cash`,
    // Counts
    BLIND_COUNT: (shiftId: string) => `/cash/shifts/${shiftId}/blind-count`,
    // Reports
    X_REPORT: (shiftId: string) => `/cash/shifts/${shiftId}/x-report`,
    Z_REPORT: (shiftId: string) => `/cash/shifts/${shiftId}/z-report`,
    DAILY_SUMMARY: '/cash/daily-summary',
  },

  // OCR scanning POC — not the real feature.
  OCR_POC: {
    SCAN: '/ocrpoc/scan',
  },

  // Cheetah message parser POC — not the real feature.
  CHEETAH_PARSER_POC: {
    PARSE: '/cheetahparsingpoc/parse',
  },
} as const;
